/**
 * lib/catalogExit.ts — where "Browse the catalog" actually goes.
 *
 * ── THE DEFECT THIS EXISTS TO CLOSE ───────────────────────────────────────
 * Sixteen exits across the org surfaces navigated to `/search`. That route
 * exists on the DESKTOP tree only: `mobile/MobileApp.tsx` has `/browse`, and
 * its `path="*"` catch-all silently redirects everything else to Home. So on a
 * phone every one of those buttons was labelled "Browse the catalog" and landed
 * on Home — and for a SIGNED-OUT visitor the app shell's auth gate bounced them
 * one step further, to `/welcome`. An exit that does not go where it says is a
 * dead end wearing a label.
 *
 * Both shells share `components/org/**` and `components/team/**` verbatim, so
 * the destination cannot be hardcoded in them. It is resolved here, once.
 *
 * ── AND WHY THE LABEL MOVES WITH THE DESTINATION ──────────────────────────
 * The app shell is auth-first by design (`isPublicPath` admits only /welcome,
 * the auth routes, the legal pages, /share/:id and /invite). A signed-out
 * visitor there genuinely cannot browse the catalog, so we do not promise it:
 * they get "Back to Diaex" → `/welcome`, which is the truth and is still a way
 * out. Desktop browses signed-out exactly as it does today, so its label and
 * its destination are unchanged.
 */
import { isAppMode } from '../mobile/lib/appMode'

export interface CatalogExit {
  path: string
  label: string
}

/**
 * @param signedIn the caller's auth state — the app shell's gate turns on it.
 * @param appMode  injectable for tests; defaults to the real shell probe.
 */
export function catalogExit(signedIn: boolean, appMode: boolean = isAppMode()): CatalogExit {
  if (!appMode) return { path: '/search', label: 'Browse the catalog' }
  if (!signedIn) return { path: '/welcome', label: 'Back to Diaex' }
  return { path: '/browse', label: 'Browse the catalog' }
}

/** Just the path, for the handful of callers that draw their own label. */
export function catalogPath(signedIn: boolean, appMode: boolean = isAppMode()): string {
  return catalogExit(signedIn, appMode).path
}
